import { useCallback } from 'react'

import { useSegment } from '../../../../../hooks'
import { FecImportTrackingEvents } from '../enums/fecImportTrackingEvents'
import type { FecImportTrackingStep } from '../utils/getFecImportTrackingStep'
import type { FecThirdPartyStatusCounts } from '../utils/getFecThirdPartyStatusCounts'

interface TrackFecImportStepParams {
  fecImportIds: string[]
  step: FecImportTrackingStep
}

interface TrackFecThirdPartyCountsParams extends FecThirdPartyStatusCounts {
  fecImportIds: string[]
}

export const useTrackFecImport = () => {
  const { track } = useSegment()

  const trackMappingsConfirmed = useCallback(
    (payload: { importCount: number }) => {
      track(FecImportTrackingEvents.MappingsConfirmed, payload)
    },
    [track],
  )

  const trackStepViewed = useCallback(
    ({ fecImportIds, step }: TrackFecImportStepParams) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Segment property names are snake_case
      track(FecImportTrackingEvents.StepViewed, { import_ids: fecImportIds, step })
    },
    [track],
  )

  const trackWizardClosed = useCallback(
    ({ fecImportIds, step }: TrackFecImportStepParams) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Segment property names are snake_case
      track(FecImportTrackingEvents.WizardClosed, { import_ids: fecImportIds, step })
    },
    [track],
  )

  const trackThirdPartyAccountsViewed = useCallback(
    ({ existingCount, fecImportIds, newCount }: TrackFecThirdPartyCountsParams) => {
      /* eslint-disable @typescript-eslint/naming-convention -- Segment property names are snake_case */
      track(FecImportTrackingEvents.ThirdPartyAccountsViewed, {
        existing_count: existingCount,
        import_ids: fecImportIds,
        new_count: newCount,
      })
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    [track],
  )

  const trackThirdPartiesConfirmed = useCallback(
    ({ existingCount, fecImportIds, newCount }: TrackFecThirdPartyCountsParams) => {
      /* eslint-disable @typescript-eslint/naming-convention -- Segment property names are snake_case */
      track(FecImportTrackingEvents.ThirdPartiesConfirmed, {
        existing_count: existingCount,
        import_ids: fecImportIds,
        new_count: newCount,
      })
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    [track],
  )

  const trackSuccessClosed = useCallback(() => {
    track(FecImportTrackingEvents.SuccessClosed)
  }, [track])

  return {
    trackMappingsConfirmed,
    trackStepViewed,
    trackSuccessClosed,
    trackThirdPartiesConfirmed,
    trackThirdPartyAccountsViewed,
    trackWizardClosed,
  }
}
